"use client";

import { useCallback } from "react";
import { useCookies } from "react-cookie";
import { useDatadog } from "@auth/client-sdk-react";
import { useSignupFlowStore } from "@./state";
import { PaymentCollectionTarget } from "@api-clients/checkout";
import { isUUID } from "class-validator";
import { buildFreshBasket } from "apps/website/utils/checkout/buildFreshBasket";
import { FormName } from "apps/website/types/form";
import { useAPI } from "apps/website/hooks/useAPI";
import {
  useSyncAccountDetails,
} from "apps/website/hooks/useSyncAccountDetails";
import {
  ACTION_TRACKING_COOKIE_NAMES,
  getActionTrackingInfo,
} from "apps/website/utils/tracking/getActionTrackingInfo";

import { SYSTEM_ERROR_MESSAGES } from "../constants";
import { CreateBasketResult } from "../types";

export function useCreateBasket() {
  const { Checkout: checkoutAPI } = useAPI();
  const datadog = useDatadog();
  const { syncAccountDetailsToUserTable } = useSyncAccountDetails(FormName.SIGN_UP_FRESH);

  const [ cookies ] = useCookies(ACTION_TRACKING_COOKIE_NAMES);
  const trackingInfo = getActionTrackingInfo(cookies);

  const { cats, user, delivery, subscription } = useSignupFlowStore((state) => ({
    user: state.user,
    cats: state.cats,
    delivery: state.delivery,
    subscription: state.subscription,
  }));

  const createBasket = useCallback(async (): Promise<CreateBasketResult> => {
    const userId = user.id;
    if (!userId) {
      const message = SYSTEM_ERROR_MESSAGES.missingCustomerIdForFlow("v2");
      datadog.logger.error(message, { eventName: "ExpressCheckoutV2MissingUserId" });
      return { success: false, message };
    }

    // Sync the latest name/email to the user table before taking payment.
    // Without this, users in the email-capture test variant (only entered name
    // on checkout contact form, not CP Details) would have an empty name in the
    // database because the initial user-row creation at CP Details didn't include one.
    const syncResult = await syncAccountDetailsToUserTable(userId);
    if (syncResult.blocked) {
      return { success: false, message: SYSTEM_ERROR_MESSAGES.EMAIL_CONFLICT };
    }

    // Use the userId returned by the sync (it may change if the backend
    // re-assigned the user row on email upsert).
    const resolvedUserId = syncResult.userId ?? userId;

    // start-checkout takes the user id in the path and rejects anything that
    // isn't a v4 UUID. If the id is unusable, stop here rather than sending a
    // request that will definitely 400.
    if (!resolvedUserId || !isUUID(resolvedUserId, "4")) {
      const message = SYSTEM_ERROR_MESSAGES.missingCustomerIdForFlow("v2");
      datadog.logger.error(message, {
        eventName: "ExpressCheckoutV2InvalidUserId",
        resolvedUserId,
        originalUserId: userId,
      });
      return { success: false, message };
    }

    const basketData = buildFreshBasket({
      cats,
      user,
      delivery,
      subscription,
      formUsed: FormName.SIGN_UP_FRESH,
      trackingInfo,
    });

    const basketResponse = await checkoutAPI.startCheckout(
      resolvedUserId,
      basketData,
      PaymentCollectionTarget.WEBSITE,
      user.persona ?? null,
    );

    if (basketResponse.validationError) {
      const message = basketResponse.validationError.message;
      datadog.logger.error(
        SYSTEM_ERROR_MESSAGES.v2CheckoutApiFailed(message),
        { eventName: "ExpressCheckoutV2ValidationError", basketResponse },
      );
      return { success: false, message };
    }

    if (basketResponse.isExisting) {
      datadog.logger.info("[useCreateBasket v2] existing basket returned", {
        eventName: "ExpressCheckoutV2ExistingBasket",
        basketId: basketResponse.basketId,
      });
      return { success: true, existingBasket: basketResponse.basketId };
    }

    if (!basketResponse.clientSecret) {
      const message = SYSTEM_ERROR_MESSAGES.v2CheckoutApiFailed("no clientSecret returned");
      datadog.logger.error(message, { eventName: "ExpressCheckoutV2NoClientSecret", basketResponse });
      return { success: false, message };
    }

    datadog.logger.info("[useCreateBasket v2] basket created successfully", {
      eventName: "ExpressCheckoutV2BasketCreated",
      basketId: basketResponse.basketId,
    });

    return {
      success: true,
      intentClientSecret: basketResponse.clientSecret,
      basketId: basketResponse.basketId,
    };
  }, [
    cats,
    user,
    delivery,
    subscription,
    trackingInfo,
    checkoutAPI,
    datadog,
    syncAccountDetailsToUserTable,
  ]);

  return { createBasket };
}
